import {
  ActionIcon,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  Popover,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import type { Icon as IconZAZA } from '@tabler/icons-react';
import { IconDotsVertical, IconLink, IconWorld, IconWorldOff } from '@tabler/icons-react';
import type { ComponentProps } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';
import { useTheme } from '@emotion/react';
import type { AxiosError } from 'axios';

import { FEATURE } from '~/common/enums/feature.enum';
import ContestCountDown from '~/components/utils/ContestCountdown';
import type { Channel } from '~/domains/commissioner/interfaces/channel.interface';
import { getCdnUrl } from '~/domains/assets/utils/getCdnUrl';
import { reportEvent } from '~/domains/analytics';
import { Avatar } from '~/domains/common/components/Avatar/Avatar';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import type { Contest } from '~/services/contests/interfaces/contest.interface';
import { getContestsByCommissionerId } from '~/services/contests/contests.service.api';
import { createNewPost } from '~/services/commissioner/commissioner-service.service.api';

import { SelfFetchingContestRow } from '../Contests/ContestRow';

type NewPostModalProps = {
  opened: boolean;
  onClose: ComponentProps<typeof Modal>['onClose'];
  channel: Channel;
  userId: string;
  contestId?: string;
};

type PublicPrivateItemProps = React.ComponentPropsWithoutRef<'div'> & {
  Icon: IconZAZA;
  label: string;
};

const SelectPublicPrivateItem = forwardRef<HTMLDivElement, PublicPrivateItemProps>(
  ({ Icon: IconComp, label, ...others }: PublicPrivateItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        <IconComp size={16} />
        <div>
          <Text size="xs">{label}</Text>
        </div>
      </Group>
    </div>
  )
);
SelectPublicPrivateItem.displayName = 'SelectPublicPrivateItem';

interface ContestItemProps extends React.ComponentPropsWithoutRef<'div'> {
  contest: Contest;
}

const SelectContestItem = forwardRef<HTMLDivElement, ContestItemProps>(
  ({ contest, ...others }: ContestItemProps, ref) => {
    const theme = useTheme();
    return (
      <div ref={ref} {...others}>
        <Stack spacing={0}>
          <Text fw={600}>{contest.name}</Text>
          <ContestCountDown
            fontSize={10}
            contestStart={contest.start_date.toString()}
            color={theme.colors.dark[2]}
          />
        </Stack>
      </div>
    );
  }
);
SelectContestItem.displayName = 'SelectContestItem';

function NewPostModal({ userId, opened, onClose, channel, contestId }: NewPostModalProps) {
  const showPublicPrivatePosts = useIsFeatureEnabled(FEATURE.SHOW_PUBLIC_PRIVATE_POSTS);
  const isAllowCommentsEnabled = useIsFeatureEnabled(FEATURE.ENABLE_POST_COMMENTS);
  const { data: contestData, isLoading: loadingContests } = useQuery({
    queryKey: ['commissioner/contests'],
    queryFn: async () =>
      getContestsByCommissionerId({
        commissionerId: userId,
        limit: 50,
        offset: 0,
      }),
  });
  const [contestPopoverOpened, setContestPopoverOpened] = useState(false);

  const contests = contestData?.data;
  const { t } = useTranslation('commissioner');
  const avatar =
    channel?.avatarUrl || getCdnUrl('/images/commissioner/DefaultCommissionerAvatar.png');

  const form = useForm({
    initialValues: {
      title: '',
      body: '',
      isPinned: false,
      allowComments: false,
      allowLikes: true,
      contestSlug: '',
      contestId: undefined,
      contestText: 'Click here',
    },
  });

  const handleSelectContest = (value) => {
    form.setFieldValue('contestSlug', value);
    form.setFieldValue('contestId', value);
    setContestPopoverOpened(false);
  };

  useEffect(() => {
    if (contestId) {
      handleSelectContest(contestId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId]);

  const { mutate: handleSubmitPost, isLoading } = useMutation({
    mutationFn: async () => {
      const values = {
        ...form.values,
      };
      return createNewPost(channel?.id, values);
    },
    onSuccess: () => {
      onClose();
      form.reset();

      reportEvent('Commissioner > Create Post', {
        channel_id: channel?.id,
        commish_id: userId,
        contest_id: contestId,
      });

      showNotification({
        message: t('hub.posts.modal.postCreateSuccess'),
        color: 'green',
        autoClose: 5000,
      });
      if (contestId) {
        handleSelectContest(contestId);
      }
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage = error?.response?.data?.message;

      reportEvent('Commissioner > Create Post Error', {
        reason: errorMessage,
      });

      showNotification({
        title: t('hub.posts.modal.postCreateError'),
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      });
    },
  });

  const isSubmitDisabled = form.values.body === '';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('hub.posts.modal.title')}
      padding="sm"
      size={600}
    >
      <Divider color="gray.2" mb="md" />
      <form onSubmit={form.onSubmit(() => handleSubmitPost())}>
        <Group position="apart" align="start">
          <Group align="start" spacing="xs">
            <Avatar src={avatar} radius={50} />
            <Stack spacing={0}>
              <Text maw={300} truncate="end" fw={700} size={12}>
                {channel?.name}
              </Text>
              {showPublicPrivatePosts && (
                <Select
                  w={150}
                  size="xs"
                  itemComponent={SelectPublicPrivateItem}
                  defaultValue="public"
                  data={[
                    {
                      label: t('hub.posts.modal.public'),
                      value: 'public',
                      Icon: IconWorld,
                    },
                    {
                      label: t('hub.posts.modal.private'),
                      value: 'private',
                      Icon: IconWorldOff,
                    },
                  ]}
                />
              )}
            </Stack>
          </Group>
          <Popover position="bottom-end" trapFocus>
            <Popover.Target>
              <ActionIcon>
                <IconDotsVertical />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack>
                {isAllowCommentsEnabled ? (
                  <Switch
                    label={t('hub.posts.modal.showComments')}
                    checked={form.values.allowComments}
                    {...form.getInputProps('allowComments')}
                  />
                ) : (
                  <Tooltip
                    label={t('hub.posts.modal.commentsComingSoon')}
                    events={{ hover: true, focus: true, touch: false }}
                  >
                    <div>
                      <Switch
                        disabled
                        label={t('hub.posts.modal.showComments')}
                        checked={form.values.allowComments}
                        {...form.getInputProps('allowComments')}
                      />
                    </div>
                  </Tooltip>
                )}
                <Switch
                  label={t('hub.posts.modal.showLikes')}
                  checked={form.values.allowLikes}
                  {...form.getInputProps('allowLikes')}
                />
                <Switch
                  label={t('hub.posts.modal.pinToTheTop')}
                  checked={form.values.isPinned}
                  {...form.getInputProps('isPinned')}
                />
              </Stack>
            </Popover.Dropdown>
          </Popover>
        </Group>
        <Stack spacing={0} mt="md">
          <TextInput
            placeholder={t('hub.posts.modal.titlePlaceholder')}
            maxLength={30}
            styles={{
              input: {
                border: 0,
                padding: 0,
                fontSize: '22px',
                fontWeight: 700,
              },
            }}
            {...form.getInputProps('title')}
          />
          <Textarea
            autoFocus
            autosize
            minRows={3}
            maxLength={255}
            w="100%"
            placeholder={t('hub.posts.modal.bodyPlaceholder')}
            styles={{
              input: {
                border: 0,
                padding: 0,
                fontSize: '16px',
              },
            }}
            {...form.getInputProps('body')}
          />
          {form.values.contestId && (
            <Card withBorder mb="lg">
              <SelfFetchingContestRow contestId={form.values.contestId} showCta={false} isStacked />
            </Card>
          )}
        </Stack>

        <Group noWrap position="apart">
          <Group>
            <Popover
              position="bottom-start"
              opened={contestPopoverOpened}
              onPositionChange={() => setContestPopoverOpened(false)}
            >
              <Popover.Target>
                <Group spacing={2} sx={{ cursor: 'pointer' }}>
                  <ActionIcon onClick={() => setContestPopoverOpened(true)}>
                    <IconLink color="black" />
                  </ActionIcon>
                  <Text onClick={() => setContestPopoverOpened(true)}>
                    {t('hub.posts.modal.linkYourContest')}
                  </Text>
                </Group>
              </Popover.Target>
              <Popover.Dropdown onMouseLeave={() => setContestPopoverOpened(false)}>
                <Stack w={400}>
                  {loadingContests ? (
                    <Loader />
                  ) : (
                    <Stack>
                      <Select
                        label={t('hub.posts.modal.contestLinkLabel')}
                        placeholder={t('hub.posts.modal.contestLinkPlaceholder')}
                        itemComponent={SelectContestItem}
                        data={
                          contests?.map((contest: Contest) => ({
                            contest,
                            label: contest.name,
                            value: contest.id,
                          })) || []
                        }
                        {...form.getInputProps('contestSlug')}
                        onChange={handleSelectContest}
                      />
                    </Stack>
                  )}
                </Stack>
              </Popover.Dropdown>
            </Popover>
          </Group>
          <Button type="submit" disabled={isSubmitDisabled} loading={isLoading}>
            <Text color={isSubmitDisabled ? 'dimmed' : 'black'}>{t('hub.posts.modal.cta')}</Text>
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

export default NewPostModal;
